import {
  getObjectTypeFromRecord,
  getS3FileURL,
  getTagFromResource,
} from "@examspark/common";
import {
  IconBookLine,
  IconCalendarLine,
  IconDocumentGraphLine,
  IconDocumentLine,
  IconExamLine,
  IconFolderLine,
  IconGlobeLine,
  IconLinkLine,
  IconMediaLine,
  IconPlayLine,
  IconQuestionCircleLine,
  IconRubricLine,
  IconUserLine,
  IconUserMultiLine,
} from "@examspark/common-ui/dist/components/icons";
import {
  ObjectType,
  Resource,
  ResourceVisibilityType,
  Workspace,
} from "@examspark/types";

export const getBankLabel = (bank: Pick<Workspace, "name" | "tags">) =>
  getTagFromResource(bank, "bonfire-label")?.value ?? bank.name;

export const getLinkForResource = (resource: Resource) => {
  if (resource.type === "WEB_DOCUMENT") {
    return ["", "webdoc", resource.id].join("/");
  }
  if (resource.type === "DOCUMENT") {
    return ["", "document", resource.id].join("/");
  }
  if (resource.type === "PERFORMANCE_TASK") {
    return ["", "assessment", resource.id].join("/");
  }
  if (resource.type === "PASSAGE") {
    return ["", "passage", resource.id].join("/");
  }
  if (resource.type === "RUBRIC") {
    return ["", "rubric", resource.id].join("/");
  }
  if (resource.type === "EXTERNAL_LINK") {
    return ["", "link", resource.id].join("/");
  }
  if (resource.type === "IMAGE" && resource.file) {
    return getS3FileURL(resource.file);
  }

  // fallback for file-based resources
  if (resource.file != null) {
    return ["", "document", resource.id].join("/");
  }

  console.log("Unknown resource type", resource);

  return "#";
};

export const getLinkForClassroomActivity = (resource: Resource) => {
  if (resource.sk === "exam-config") {
    const unitId = getTagFromResource(resource, "unit-id")?.value;
    const classroomId = getTagFromResource(resource, "classroom-id")?.value;

    if (unitId && classroomId) {
      return [
        "",
        "classroom",
        classroomId,
        "unit",
        unitId,
        "activities",
        resource.id,
      ].join("/");
    }
  }
};

export const getVisibilityDescription = (
  visibility: ResourceVisibilityType | undefined,
) => {
  switch (visibility) {
    case ResourceVisibilityType.Private:
      return {
        label: "Just Me",
        visibility: ResourceVisibilityType.Private,
        IconComponent: IconUserLine,
      };
    case ResourceVisibilityType.Workspace:
      return {
        label: "My Workspace",
        visibility: ResourceVisibilityType.Workspace,
        IconComponent: IconUserMultiLine,
      };
    case ResourceVisibilityType.WorkspaceShareable:
    case undefined:
      return {
        label: "My Workspace + Workspaces I Share With",
        visibility: ResourceVisibilityType.WorkspaceShareable,
        IconComponent: IconGlobeLine,
      };
  }
};

export const getIconForItem = (item: Resource | undefined) =>
  getIconForObjectType(getObjectTypeFromRecord(item));

export const getIconForObjectType = (
  type?: ObjectType,
): React.FC<React.PropsWithChildren<unknown>> => {
  if (type == null) return IconQuestionCircleLine;

  switch (type) {
    case "DOCUMENT":
      return IconDocumentLine;
    case "WEB_DOCUMENT":
      return IconFolderLine;
    case "EXTERNAL_LINK":
      return IconLinkLine;
    case "SPREADSHEET":
      return IconDocumentGraphLine;
    case "IMAGE":
      return IconMediaLine;
    case "AUDIO":
    case "VIDEO":
      return IconPlayLine;
    case "PASSAGE":
      return IconBookLine;
    case "ACADEMIC_CALENDAR":
      return IconCalendarLine;
    case "EXAM":
    case "ITEM":
      return IconExamLine;
    case "RUBRIC":
      return IconRubricLine;
    default:
      return IconDocumentLine;
  }
};

export const getBrowseForItem = (item: Resource | undefined) => {
  if (item == null)
    return {
      label: "Unknown",
      url: undefined,
      IconComponent: IconQuestionCircleLine,
    };

  const type = getObjectTypeFromRecord(item);

  return {
    label: getLabelForObjectType(type),
    url: getUrlForObject(item) ?? getLinkForResource(item),
    IconComponent: getIconForObjectType(type),
  };
};

export const getLabelForObjectType = (type?: ObjectType) => {
  switch (type) {
    case "ITEM":
      return "Assessment Item";
    case "PASSAGE":
      return "Passage";
    case "COURSE":
      return "Course";
    case "EXAM":
      return "Assessment";
    case "UNIT":
      return "Unit / Module";
    case "PERFORMANCE_TASK":
      return "Performance Assessment";
    case "RUBRIC":
      return "Rubric";
    case "VOCABULARY":
      return "Vocabulary";
    case "CLASSROOM":
      return "Classroom";
    case "CLARIFICATION":
      return "Clarification Document";
    case "CONCEPTUAL_ALIGNMENT":
      return "Conceptual Alignment Document";
    case "VERTICAL_ALIGNMENT":
      return "Vertical Alignment Document";
    case "COURSE_VERIFICATION":
      return "Verification Document";
    case "ACADEMIC_CALENDAR":
      return "Academic Calendar";
    case "COURSE_CALENDAR":
      return "Course Calendar";
    case "COURSE_PLAN":
      return "Year At A Glance";
    case "EXTERNAL_LINK":
      return "External Link";
    case "WEB_DOCUMENT":
      return "Collection";
    case "DOCUMENT":
      return "Document";
    case "IMAGE":
      return "Image";
    case "AUDIO":
      return "Audio";
    case "VIDEO":
      return "Video";
    case "ISSUE":
      return "Issue";
    case "TASK":
      return "Task";
    case "SPREADSHEET":
      return "Spreadsheet";
    default:
      return "Unknown";
  }
};

export const getUrlForObject = (
  item: Pick<Resource, "id" | "sk" | "type"> | undefined,
  type?: ObjectType,
) => {
  if (item == null) return undefined;

  type = type ?? getObjectTypeFromRecord(item);

  switch (type) {
    case "CLARIFICATION":
    case "CONCEPTUAL_ALIGNMENT":
    case "VERTICAL_ALIGNMENT":
      return `/standardsview/${item.id}`;
    case "UNIT":
      return `/unit/${item.id}`;
    case "ITEM":
      return `/item/${item.id}`;
    case "COURSE":
      return `/course/${item.id}`;
    case "EXAM":
      return `/exam/${item.id}`;
    case "PERFORMANCE_TASK":
      return `/assessment/${item.id}`;
    case "RUBRIC":
      return `/rubric/${item.id}`;
    case "VOCABULARY":
      return `/vocabulary/${item.id}`;
    case "CLASSROOM":
      return `/classroom/${item.id}`;
    case "COURSE_VERIFICATION":
      return `/course/${item.id}/verification`;
    case "COURSE_PLAN":
      return `/course/${item.id}/year`;
    case "ACADEMIC_CALENDAR":
      return `/calendar/workspace/${item.id}`;
    case "COURSE_CALENDAR":
      // @ts-expect-error need to fix the type here
      return `/calendar/course/${item.courseId}/${item.id}`;
    default:
      return undefined;
  }
};
